import { OutOfDateGuard, refusedOrVersion, withBasedOn } from 'api/keptVersion';
import { leaveSignedOut } from 'utils/signedOut';
import { getCurrentAuthToken } from 'utils/authHelpers';
import { getLocalAppSession } from 'utils/storageHelpers';
import { PreferencesPayload } from 'views/preferences/PreferencesConfigTypes';

/**
 * The campaign preferences store themselves (auto-save).
 *
 * This is one of the four screens WITHOUT a publish moment (D34): whatever
 * is stored here is in use at once (the name in the menu, the mails to
 * candidates, the notifications). So today's rule stays: while a required
 * field is empty the screen is not stored at all, and the corner says why
 * (D35).
 */

/** The screen's work, by the form's own field names. */
export interface PreferencesWork {
  name: string;
  lead_recruiter: number;
  hide_from_careers_page: boolean;
  email_on_candidate: boolean;
  email_on_communication: boolean;
  /** A mail template's number; the text 'null' when that mail is off. */
  vacancy_template_done_survey?: number | string | null;
  vacancy_template_ask_for_cv?: number | string | null;
  vacancy_template_got_cv?: number | string | null;
}

/** What is missing, in the words the corner shows after "Not saved: ". */
export const missingForPreferences = (work: PreferencesWork): string[] => [
  ...(String(work.name ?? '').trim() === ''
    ? ['the name of this campaign is required']
    : []),
  // Required by the form today as well (ConfigInput, `requiredFields`).
  ...(!work.lead_recruiter ? ['a lead recruiter is required'] : []),
];

/**
 * A mail that is switched off travels as "none". A mail the server did not
 * hand out at all is LEFT OUT, as the Save button left it out: sent as
 * "none" the server would switch that mail off (review of the slice).
 */
const template = (
  value: number | string | null | undefined
): number | null | undefined => {
  if (value === undefined) return undefined;
  return value === 'null' || value === null ? null : Number(value);
};

/** The work as the server takes it: the same body the Save button sent. */
export const preferencesBody = (
  work: PreferencesWork
): PreferencesPayload['input'] => ({
  name: work.name,
  lead_recruiter_id: work.lead_recruiter ?? 0,
  excluded_from_careers_page: work.hide_from_careers_page,
  notification_preferences: {
    email_on_candidate: work.email_on_candidate,
    email_on_communication: work.email_on_communication,
  },
  vacancy_templates: {
    // The Save button sent this one undefined (the form has no such
    // field); the server does not read it.
    enabled: undefined as unknown as boolean,
    done_survey: template(work.vacancy_template_done_survey) as number | null,
    ask_for_cv: template(work.vacancy_template_ask_for_cv) as number | null,
    got_cv: template(work.vacancy_template_got_cv) as number | null,
  },
});

export const preferencesPath = (vacancyId: number): string =>
  `/v4/vacancies/${vacancyId}/preferences`;

/** Stores the preferences, quietly. @throws when the server did not confirm */
export const savePreferences = async (
  vacancyId: number,
  work: PreferencesWork,
  signal?: AbortSignal,
  guard?: OutOfDateGuard
): Promise<void> => {
  const base = (import.meta.env.VITE_BASE_URL_API as string) ?? '';
  const response = await fetch(`${base}${preferencesPath(vacancyId)}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${getCurrentAuthToken('preserve')}`,
      'Active-Customer': String(getLocalAppSession()?.activeCustomer ?? ''),
    },
    body: JSON.stringify(withBasedOn(preferencesBody(work), guard?.basedOn)),
    signal,
  });
  if (response.status === 401) {
    if (window.location.pathname !== '/') {
      leaveSignedOut();
    }
    throw new Error('The session has ended.');
  }
  // D10: out of date is not a failure; the person chooses.
  const version = await refusedOrVersion(response);
  if (!response.ok) {
    throw new Error(`The preferences were not stored (${response.status}).`);
  }
  const answer = (await response.json().catch(() => ({}))) as {
    success?: boolean;
  };
  if (answer.success === false) {
    throw new Error('The preferences were not stored.');
  }
  guard?.stored(version);
};
